import React from "react";
import { SkeletonItem } from "@fluentui/react-components";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";

// `cof.base.loadingdata` is not defined in outlineCaptions.ts - it never moved there for IDS-7053 either. Per
// FR-012 and the caption inventory note (docs/IDS-7054-view-component-documents/08-captions.md §2) it keeps its
// key but changes role here: from the IDS-7053 spinner's visible label to this skeleton's accessible name. It is
// referenced directly by key, exactly like ChangeComponentSource.tsx does today for the spinner this replaces.
const CAPTION_LOADING_DATA = "cof.base.loadingdata";

// Fixed by design: while the component-instance lookup is in flight the real number of source-document cards is
// unknown, so the placeholder count can never be derived from data (see T5 "Must NOT do").
const PLACEHOLDER_CARD_COUNT = 3;

/**
 * Card-shaped loading placeholder for the Change Component Source page (IDS-7054 FR-012, architecture decision
 * D8). It replaces the IDS-7053 spinner with a fixed number of skeleton cards while the component-instance
 * lookup is in flight (U12). Each card approximates the shape of a real source-document card: a wide bar for
 * the read-only name field, a thin bar for its SharePoint path, a small square for the disabled search control,
 * and a short bar for the show/hide instances toggle.
 *
 * The `<output>` wrapper keeps the `change-component-source-loading` test id and the implicit "status" role
 * that ChangeComponentSource's loading branch already exposes, so the existing loading test keeps passing once
 * this component replaces the spinner there.
 */
export function SourceListSkeleton() {
  return (
    <output
      aria-busy="true"
      aria-label={formatMessage(CAPTION_LOADING_DATA)}
      data-testid="change-component-source-loading"
      style={{ display: "flex", flexDirection: "column", width: "100%" }}
    >
      {Array.from({ length: PLACEHOLDER_CARD_COUNT }).map((_, index) => (
        <div className="card component_source_card" data-testid="source-list-skeleton-card" key={index}>
          <div style={{ alignItems: "center", display: "flex", gap: 10 }}>
            <div style={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0 }}>
              {/* Wide bar: stands in for the read-only source-document name field. Sized at 32px - the same
                  min-height SourceDocumentCard.tsx now gives the real field to match Fluent's standard TextField
                  field-group height (see docs/IDS-7054-view-component-documents/02-architecture.md §7.2) - so
                  this placeholder keeps the real card's footprint (P8) once the skeleton is replaced. */}
              <SkeletonItem animation="pulse" shape="rectangle" style={{ height: 32 }} />
              {/* Thin bar: stands in for the containing-folder path below the document name. */}
              <SkeletonItem animation="pulse" shape="rectangle" style={{ height: 12, marginTop: 4, width: "45%" }} />
            </div>
            {/* Small square: stands in for the disabled search control next to the name. */}
            <SkeletonItem animation="pulse" shape="square" size={32} />
          </div>
          {/* Short bar: stands in for the show/hide instances toggle. */}
          <SkeletonItem animation="pulse" shape="rectangle" style={{ height: 16, marginTop: 10, width: "25%" }} />
        </div>
      ))}
    </output>
  );
}
